/**
 * @fileoverview Hydrating the registry for the signed-in administrator.
 *
 * The seam between our authorization model and the extension contract. It is
 * small on purpose: `hydrateExtensionRegistry` takes a set of operation strings
 * and knows nothing about roles, sessions or Cedar, which is what makes it
 * testable without a browser and replaceable without touching a page.
 *
 * TODAY THE MAPPING IS COARSE and this is the one place that says so. Our
 * authorization is role-shaped — `platform_admin` or not — while the contract
 * is operation-shaped, so a platform admin is granted every operation the
 * registry asks for and everyone else gets none of them. That is exactly the
 * behaviour the hard-coded `requiresRole: "platform_admin"` had, so nothing
 * regressed; what changed is that per-operation grants are now a change to this
 * function rather than a change to every page.
 */

import { useEffect, useMemo, useState } from "react";

import { DEFAULT_EXTENSION_REGISTRY } from "./DefaultExtensionRegistry";
import type { ControlPanelExtensionDefinition, HydratedExtension } from "./ExtensionDefinition";
import { hydrateExtensionRegistry, type HydrationContext } from "./hydrateExtensionRegistry";

/** Every operation any shipped definition asks for. */
function allDeclaredOperations(
  registry: readonly ControlPanelExtensionDefinition[],
): ReadonlySet<string> {
  const ops = new Set<string>();
  for (const d of registry) {
    for (const op of d.visibility.conjunctiveOperations) ops.add(op);
    for (const op of d.visibility.disjunctiveOperations) ops.add(op);
  }
  return ops;
}

export function operationsForRole(
  role: string | null | undefined,
  registry: readonly ControlPanelExtensionDefinition[] = DEFAULT_EXTENSION_REGISTRY,
): ReadonlySet<string> {
  return role === "platform_admin" ? allDeclaredOperations(registry) : new Set<string>();
}

export interface UseHydratedRegistryResult {
  /** Every definition with its verdict, sorted by display name. */
  readonly extensions: readonly HydratedExtension[];
  /** True until the first hydration settles. */
  readonly isLoading: boolean;
}

export function useHydratedRegistry(
  role: string | null | undefined,
  registry: readonly ControlPanelExtensionDefinition[] = DEFAULT_EXTENSION_REGISTRY,
): UseHydratedRegistryResult {
  const [extensions, setExtensions] = useState<readonly HydratedExtension[]>([]);
  const [isLoading, setLoading] = useState(true);

  const context = useMemo<HydrationContext>(
    () => ({
      operations: operationsForRole(role, registry),
      // Everything the definitions can require is served by one API today, so
      // an empty set here would hide every page that names a service. Declared
      // rather than probed: a real availability check belongs behind the port,
      // not behind a guess made in a hook.
      availableServices: new Set(["ironcase-api"]),
      // No space scope in this product yet; `space`-typed definitions would be
      // filtered out, which is the correct answer rather than a missing case.
      resolvableTypes: new Set(["global", "enrollment", "organization"] as const),
      /*
        NOTHING IS RESTRICTED, DELIBERATELY.

        This is the `extension-restrictions` stage — the one whose recovered
        failure text is "not entitled (extension restrictions)", and the one
        that puts the ENTERPRISE badge on the tenant's All Settings. It is a
        runtime entitlement, not static data: there is no `enterprise` or `tier`
        field anywhere in the 94 recovered definitions.

        We own this deployment, so we are entitled to all of them. The ~91
        settings we do not yet serve are LOCKED BECAUSE THEY ARE UNBUILT, which
        is a backlog, not a licensing wall — and the distinction matters,
        because one of those is work we can do and the other is a cheque.

        Left as an explicit empty set rather than omitted: an operator turning a
        setting off for this deployment is a real future need, and this is where
        it goes.
      */
      restrictedIds: new Set<string>(),
    }),
    [role, registry],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void hydrateExtensionRegistry(registry, context).then((result) => {
      // The viewer can change mid-flight — sign out, switch agency — and a
      // stale resolution must not overwrite a fresh one.
      if (cancelled) return;
      setExtensions(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [registry, context]);

  return { extensions, isLoading };
}
